import { useCallback, useRef } from 'react';
import { calculateCustomBetFilter } from '@/api/custom-bets';
import {
  buildCalculateRequestSelections,
  findCustomItemIndex,
} from '@/domain/custom-bets';
import {
  betslipApplyCustomOdd,
  betslipSetPossibleWinPending,
  betslipUndoLastCustomSelection,
  getBetslipTicket,
} from './betslip-store';
import {
  clearCustomBetAvailableSelections,
  getCustomBetCalcSeq,
  nextCustomBetCalcSeq,
  setCustomBetAvailableSelections,
} from './custom-bet-availability-store';

export interface CustomBetPricingResult {
  ok: boolean;
  odds?: number;
  cannotCombine?: boolean;
  error?: unknown;
}

/**
 * Reprice a Custom package via CBAPI `calculate-filter` (legacy `RefreshCustomBetPricing`).
 */
export function useCustomBetPricing() {
  const inflightRef = useRef(0);

  const refresh = useCallback(async (eventId: string | number): Promise<CustomBetPricingResult> => {
    const seq = nextCustomBetCalcSeq();
    inflightRef.current = seq;

    const ticket = getBetslipTicket();
    const itemIndex = findCustomItemIndex(ticket, eventId);
    if (itemIndex < 0) {
      clearCustomBetAvailableSelections();
      betslipSetPossibleWinPending(false);
      return { ok: true };
    }

    const bet = ticket.Items[itemIndex]!.Bets.find((entry) => entry.Custom === true);
    const selections = bet?.Selections ?? [];
    if (selections.length === 0) {
      betslipApplyCustomOdd(eventId, null);
      clearCustomBetAvailableSelections();
      return { ok: true };
    }

    // Call calculate-filter from the first leg so AvailableSelections can lock conflicts.
    // Combo package odd is only applied once there are at least two legs.
    betslipSetPossibleWinPending(true);

    try {
      const response = await calculateCustomBetFilter({
        Selections: buildCalculateRequestSelections(selections),
      });

      if (seq !== getCustomBetCalcSeq()) {
        return { ok: true };
      }

      const odds = response.Odds;
      if (odds === 0) {
        betslipUndoLastCustomSelection(eventId);
        clearCustomBetAvailableSelections();
        // Reprice after undo (legacy recursive refresh).
        if (findCustomItemIndex(getBetslipTicket(), eventId) >= 0) {
          void refresh(eventId);
        }
        return { ok: false, cannotCombine: true, odds: 0 };
      }

      setCustomBetAvailableSelections(response.AvailableSelections ?? null);
      if (selections.length < 2) {
        betslipApplyCustomOdd(eventId, null);
        betslipSetPossibleWinPending(true);
        return { ok: true };
      }

      betslipApplyCustomOdd(eventId, odds);
      return { ok: true, odds };
    } catch (error) {
      if (seq !== getCustomBetCalcSeq()) {
        return { ok: true };
      }
      betslipSetPossibleWinPending(false);
      return { ok: false, error };
    }
  }, []);

  return { refreshCustomBetPricing: refresh };
}
